import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { useQuery } from "react-query";
import { OrganizationChart } from "primereact/organizationchart";
import { ChartToggler } from "@caelor/cards-and-panels-components";
import { useStructureStore } from "../../../optics/components/drawers/settings/features/org-chart/store/structure-store";
import { useSidebarStore } from "../../../optics/components/drawers/settings/features/org-chart/store/sidebar-store";
import { useActionsStore } from "../../../optics/components/drawers/settings/features/org-chart/store/actions-store";
import { getFields, getRecordsById } from "../../../api/cosmos-api";
import { filterManualTreeBasedOnLevel } from "../edit/structure-builder";
import PanAndZoomWrapper from "../PanAndZoom/PanAndZoom";
import eventBus from "../eventBus";
import { ChartContext } from "../context";
import UserTemplate from "./UserTemplate";
import { useOrgChartStore } from "../../../stores/use-org-chart-store";
import CustomEmptyState from "../../../components/empty-state/CustomEmptyState";
import { CUSTOM_EMPTY_STATE_KEY } from "../../../components/empty-state/custom-templates";
import { useOpticsStore } from "../../../stores/use-optics-store";
import { useStyleguideColors } from "../../../hooks/use-styleguide-colors";
import ZoomController from "./ZoomController";
import { createCenterViewHandler } from "./handleCenterView";

export const Wrapper = styled.div`
  height: ${({ isPreview }) => (isPreview ? "calc(100% - 50px)" : "100%")};
  overflow: hidden;

  ${({ isPreview }) => {
    if (isPreview) {
      return `
        min-height: 500px;
        padding-bottom: 50px;

        .p-organizationchart-table {
          cursor: grab;

          &:active {
            cursor: grabbing;
          }
        }
    `;
    }
  }}

  .p-node-toggler {
    ${({ isPreview }) => {
      if (!isPreview) {
        return "left: calc(50% - 28px);";
      }
    }}
  }

  .p-organizationchart > .p-organizationchart-table:first-child > tbody {
    ${({ hasMultipleFirstLevelNodes }) => {
      return `
          & > tr:nth-child(-n + ${hasMultipleFirstLevelNodes ? "2" : "3"}) {
            visibility: hidden !important;
          }
        `;
    }}
  }

  .p-organizationchart-line-down {
    width: 2px;
    background-color: ${({ connectingLineColor }) => connectingLineColor};
  }

  .p-organizationchart-lines {
    .p-organizationchart-line-right.p-organizationchart-line-top:not(:nth-child(2)):not(:nth-last-child(2)) {
      border-top: ${({ connectingLineColor }) =>
        `2px solid ${connectingLineColor}`};
      border-left: ${({ connectingLineColor }) =>
        `2px solid ${connectingLineColor}`};
    }
    .p-organizationchart-line-left.p-organizationchart-line-top:not(:nth-child(2)):not(:nth-last-child(2)) {
      border-top: ${({ connectingLineColor }) =>
        `2px solid ${connectingLineColor}`};
      border-right: ${({ connectingLineColor }) =>
        `2px solid ${connectingLineColor}`};
    }

    .p-organizationchart-line-right.p-organizationchart-line-top:not(:not(:nth-child(2)):not(:nth-last-child(2))) {
      border-top: ${({ connectingLineColor }) =>
        `2px solid ${connectingLineColor}`};
      border-left: ${({ connectingLineColor }) =>
        `2px solid ${connectingLineColor}`};
    }
    .p-organizationchart-line-left.p-organizationchart-line-top:not(:not(:nth-child(2)):not(:nth-last-child(2))) {
      border-top: ${({ connectingLineColor }) =>
        `2px solid ${connectingLineColor}`};
      border-right: ${({ connectingLineColor }) =>
        `2px solid ${connectingLineColor}`};
    }
  }
`;

function ManualOrgChart({ isPreview = false, structure, viewOptions }) {
  const [userRecords, setUserRecords] = useState([]);
  const [fields, setFields] = useState([]);
  const chartContainerRef = useRef();
  const [zoom, setZoom] = useState(1);

  const { data } = useOrgChartStore((state) => ({
    data: state.data,
    updateConfig: state.updateConfig,
  }));

  const { styleguideSetup, isCosmosInstalled } = useOpticsStore((state) => ({
    styleguideSetup: state.styleguideSetup,
    isCosmosInstalled: state.isCosmosInstalled,
  }));

  const styleguideColors = useStyleguideColors({
    styleguideSetup,
  });

  const [filteredStructure, setFilteredStructure] = useState();

  const [resizedInit, setResizedInit] = useState(false);

  const chartRef = useRef();

  const theme = useTheme();

  const {
    updateNodeExpandedState,
    selectedUser,
    setSelectedUser,
    removeUser,
    getLevelsWithUsers,
    selectedLevel,
  } = useStructureStore((state) => ({
    updateNodeExpandedState: state.updateNodeExpandedState,
    selectedUser: state.selectedUser,
    setSelectedUser: state.setSelectedUser,
    removeUser: state.removeUser,
    getLevelsWithUsers: state.getLevelsWithUsers,
    selectedLevel: state.selectedLevel,
  }));

  const { setIsSidebarOpen } = useSidebarStore((state) => ({
    setIsSidebarOpen: state.setIsSidebarOpen,
  }));

  const { setIsAddingPeople } = useActionsStore((state) => ({
    setIsAddingPeople: state.setIsAddingPeople,
  }));

  const { isLoading: clientExistsLoading, data: clientExists } = useQuery(
    ["cosmos-client-exists"],
    {
      retry: 0,
      select: (response) => {
        const { data } = response;
        return data;
      },
    }
  );

  const { isLoading: fieldsLoading, data: fieldsData } = useQuery(
    ["cosmos-fields", { includeUserFields: false }],
    getFields,
    {
      enabled: !clientExistsLoading && clientExists,
      retry: 0,
      select: (response) => {
        const { data } = response;
        return data;
      },
    }
  );

  useEffect(() => {
    if (!fieldsLoading && fieldsData) {
      setFields([...fieldsData]);
    }

    return () => {
      setFields([]);
    };
  }, [fieldsData, fieldsLoading]);

  useEffect(() => {
    if (chartRef?.current && !resizedInit) {
      eventBus.dispatch("caelor-handle-center-view");
      setResizedInit(true);
    }
  }, [chartRef, resizedInit]);

  const handleAddUsers = (e, { id, name }) => {
    e.preventDefault();
    e.stopPropagation();

    setIsSidebarOpen(true);

    setSelectedUser({ id, name });
    setIsAddingPeople(true);
  };

  const handleDeleteUser = (userId) => {
    removeUser(userId);
  };

  const fetchRecords = async (users) => {
    try {
      const response = await getRecordsById(users.map((user) => user.id));
      return response?.data?.hits;
    } catch (error) {
      return undefined;
    }
  };

  const getUserRecordDetails = useCallback(async (levelsWithUsers) => {
    const promises = levelsWithUsers.map(async ({ users }) =>
      fetchRecords(users)
    );
    const allUserRecords = await Promise.all(promises)
      .then((res) => res.flat().filter((user) => !!user))
      .catch(() => []);
    return allUserRecords;
  }, []);

  useEffect(() => {
    const levelsWithUsers = getLevelsWithUsers();

    if (levelsWithUsers?.length) {
      (async () => {
        const allUserRecords = await getUserRecordDetails(levelsWithUsers);
        setUserRecords([...allUserRecords]);
      })();
    } else {
      setUserRecords([]);
    }

    return () => {
      setUserRecords([]);
    };
  }, [structure, getUserRecordDetails, getLevelsWithUsers]);

  useEffect(() => {
    if (selectedLevel) {
      const filteredTree = filterManualTreeBasedOnLevel(
        structure,
        selectedLevel
      );
      setFilteredStructure({ ...filteredTree });
    } else {
      setFilteredStructure({ ...structure });
    }
    if (isPreview) {
      setTimeout(() => {
        eventBus.dispatch("caelor-handle-center-view", {
          minZoomOnCenterView: 0.7,
        });
      }, 200);
    }
  }, [selectedLevel, structure, isPreview]);

  useEffect(() => {
    const handler = createCenterViewHandler({
      chartContainerRef,
      zoom,
      setZoom,
      selectedUser,
    });

    eventBus.on("caelor-handle-center-view", handler);
    return () => eventBus.remove("caelor-handle-center-view", handler);
  }, [zoom, selectedUser]);

  if (!structure?.children?.length || !filteredStructure?.children?.length) {
    return (
      <CustomEmptyState templateKey={CUSTOM_EMPTY_STATE_KEY.ORG_CHART_SETUP} />
    );
  }

  return (
    <ChartContext.Provider
      value={{
        isPreview,
        userRecords,
        fields,
        viewOptions,
        isCosmosInstalled,
        handleAddUsers,
        handleDeleteUser,
      }}
    >
      <PanAndZoomWrapper
        containerRef={chartContainerRef}
        zoom={zoom}
        isPreview={isPreview}
        mainWrapperStyle={{ height: "100%" }}
      >
        <Wrapper
          isPreview={isPreview}
          connectingLineColor={
            data.config?.lineColor?.light ||
            styleguideColors?.accent?.light ||
            "#4C9AFF"
          }
          hasMultipleFirstLevelNodes={structure?.children?.length > 1}
          id="translate-wrapper"
          ref={chartContainerRef}
        >
          <OrganizationChart
            ref={chartRef}
            value={[filteredStructure]}
            nodeTemplate={UserTemplate}
            togglerIcon={ChartToggler}
            pt={{
              nodeTogglerIcon: {
                isPreview,
                updateNodeExpandedState,
                primaryColor: theme.orgchart.minusPlusColor,
              },
            }}
          />
        </Wrapper>
      </PanAndZoomWrapper>

      <ZoomController zoom={zoom} setZoom={setZoom} />
    </ChartContext.Provider>
  );
}

export default ManualOrgChart;
